import { useSecondaryPage } from '@/PageManager'
import ContentPreview from '@/components/ContentPreview'
import Note from '@/components/Note'
import NoteInteractions from '@/components/NoteInteractions'
import NoteStats from '@/components/NoteStats'
import UserAvatar from '@/components/UserAvatar'
import RepostNoteCard from '@/components/NoteCard/RepostNoteCard'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useAsync } from 'react-use'
import client from '@/services/client.service'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { arePointersToSameEvent, getParents, getRoot } from '@/lib/event'
import { toNote } from '@/lib/link'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { EllipsisIcon } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { ExternalPointer } from '@nostr/tools/nip22'
import { forwardRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ClientSelect from '@/components/ClientSelect'
import { getKindDescription } from '@/lib/nostr-kinds-registry'
import { loadNostrUser } from '@nostr/gadgets/metadata'

const NotePage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const { value: event, loading: isFetching } = useAsync(() => client.fetchEvent(id), [id])
  const [repostTargetEvent, setRepostTargetEvent] = useState<Event | null>(null)

  const { value } = useAsync(async () => {
    if (!event) return { root: undefined, parents: [] }
    return {
      root: await getRoot(event),
      parents: getParents(event).filter((parent): parent is EventPointer | AddressPointer =>
        root ? !arePointersToSameEvent(parent, root) : !('value' in parent)
      )
    }
  }, [event])
  const { root, parents } = value || { root: undefined, parents: [] }

  // page title logic
  useAsync(async () => {
    if (!event) return
    let cancelled = false

    const [profile, kindDescription] = await Promise.all([
      loadNostrUser(event.pubkey).then((p) => {
        if (p) client.addUsernameToIndex(p)
        return p
      }),
      event.kind === 1 ? 'Note' : getKindDescription(event.kind)
    ])
    if (cancelled) return

    const shortAuthor =
      profile.shortName.length > 12
        ? profile.shortName.slice(0, 11).trim() + '…'
        : profile.shortName

    let shortContent = (
      [1, 11, 1111].includes(event.kind)
        ? event.content
        : event.tags.find(([t]) => t === 'title')?.[1] ||
          event.tags.find(([t]) => t === 'name')?.[1] ||
          event.tags.find(([t]) => t === 'description')?.[1] ||
          ''
    )
      .replace(/\n/g, ' ')
      .trim()

    const budget = 30 - shortAuthor.length
    if (shortContent && shortContent.length > budget)
      shortContent = shortContent.slice(0, budget - 1).trim() + '…'

    document.title = shortContent
      ? t('{{kind}} from {{shortAuthor}}: "{{shortContent}}" on {{appName}}', {
          kind: kindDescription,
          shortAuthor,
          shortContent,
          appName: window.hallway.universe.appName
        })
      : t('{{kind}} from {{shortAuthor}} on {{appName}}', {
          kind: kindDescription,
          shortAuthor,
          appName: window.hallway.universe.appName
        })

    return () => {
      cancelled = true
    }
  }, [event])

  if (!event && isFetching) {
    return (
      <SecondaryPageLayout ref={ref} index={index} title={t('Note')}>
        <div className="px-4 pt-3">
          <div className="flex items-center space-x-2">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className={`flex-1 w-0`}>
              <div className="py-1">
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="py-0.5">
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          </div>
          <div className="pt-2">
            <div className="my-1">
              <Skeleton className="w-full h-4 my-1 mt-2" />
            </div>
            <div className="my-1">
              <Skeleton className="w-2/3 h-4 my-1" />
            </div>
          </div>
        </div>
      </SecondaryPageLayout>
    )
  }
  if (!event) {
    return (
      <SecondaryPageLayout ref={ref} index={index} title={t('Note')} displayScrollToTopButton>
        <div className="text-muted-foreground w-full h-full flex flex-col items-center justify-center gap-2">
          <div>{t('Note not found')}</div>
          <ClientSelect originalNoteId={id} />
        </div>
      </SecondaryPageLayout>
    )
  }

  // Handle repost events specially
  // TODO: just make this a new event rendering view like Reaction
  if (event.kind === kinds.Repost) {
    return (
      <SecondaryPageLayout ref={ref} index={index} title={t('Note')} displayScrollToTopButton>
        <div className="pt-3">
          <RepostNoteCard
            event={event}
            className=""
            filterMutedNotes={false}
            onTargetEventLoaded={setRepostTargetEvent}
          />
        </div>
        <NoteInteractions
          key={`note-interactions-${repostTargetEvent?.id ?? event.id}`}
          pageIndex={index}
          event={repostTargetEvent ?? event}
        />
      </SecondaryPageLayout>
    )
  }

  return (
    <SecondaryPageLayout ref={ref} index={index} title={t('Note')} displayScrollToTopButton>
      <div className="px-4 pt-3">
        {root && 'value' in root && (
          <div>
            <Card
              className="flex space-x-1 px-1.5 py-1 items-center cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => {
                /* push(toNoteList({ external: root })) */
              }}
            >
              <div className="truncate">{root.value}</div>
            </Card>
            <div className="ml-5 w-px h-2 bg-border" />
          </div>
        )}
        {root && !('value' in root) && !parents.some((p) => arePointersToSameEvent(root, p)) && (
          <ParentNote pointer={root} />
        )}
        <div
          className="grid gap-2 shrink-0"
          style={{ gridTemplateColumns: `repeat(${parents.length}, minmax(0, 1fr))` }}
        >
          {parents.map((parent) => (
            <ParentNote
              key={'id' in parent ? parent.id : parent.identifier}
              pointer={parent}
              rootToCompare={root}
            />
          ))}
        </div>
        {(!!root || parents.length > 0) && <div className="ml-5 w-px h-3 bg-border" />}
        <Note
          event={event}
          className="select-text"
          hideParentNotePreview
          originalNoteId={id}
          showFull
        />
        <NoteStats className="mt-3" event={event} fetchIfNotExisting />
      </div>
      <Separator className="mt-4" />
      <NoteInteractions key={`note-interactions-${event.id}`} pageIndex={index} event={event} />
    </SecondaryPageLayout>
  )
})

NotePage.displayName = 'NotePage'
export default NotePage

function ParentNote({
  pointer,
  rootToCompare
}: {
  pointer: Event | EventPointer | AddressPointer

  // given that this is a parent and we want to check if it's consecutive to the root
  rootToCompare?: ExternalPointer | EventPointer | AddressPointer
}) {
  const { push } = useSecondaryPage()
  const { value: event, loading: isFetching } = useAsync(async () => {
    if (!pointer) return undefined
    if ('sig' in pointer) return pointer
    return client.fetchEvent(pointer)
  }, [pointer])

  const isConsecutive = useMemo(() => {
    if (!event || !rootToCompare) return undefined
    if (arePointersToSameEvent(rootToCompare, event)) return undefined
    return getParents(event).some((grandparent) =>
      arePointersToSameEvent(rootToCompare, grandparent)
    )
  }, [event, rootToCompare])

  return (
    <div>
      {isConsecutive === true ? (
        <div className="ml-5 w-px h-3 bg-border" />
      ) : !event || isConsecutive === false ? (
        <HugeiconsIcon icon={EllipsisIcon} className="ml-3.5 text-muted-foreground/60 size-3" />
      ) : null}
      {isFetching ? (
        <div>
          <div className="flex space-x-1 px-[0.4375rem] py-1 items-center rounded-full border cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30 text-sm text-muted-foreground min-w-0 max-w-fit">
            <Skeleton className="shrink w-4 h-4 rounded-full" />
            <div className="py-1 flex-1">
              <Skeleton className="h-3" />
            </div>
          </div>
          <div className="ml-5 w-px h-3 bg-border" />
        </div>
      ) : (
        <div
          className={cn(
            'flex space-x-1 px-[0.4375rem] py-1 items-center rounded-full border cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30 text-sm text-muted-foreground min-w-0 max-w-fit',
            event && 'hover:text-foreground'
          )}
          onClick={() => {
            push(toNote(pointer))
          }}
        >
          {event && <UserAvatar userId={event.pubkey} size="tiny" className="shrink-0" />}
          <ContentPreview className="truncate" event={event} />
        </div>
      )}
    </div>
  )
}
